import { Stack, Tooltip, Typography } from '@mui/material';
import { AnalyticsEventName, trackAmplitudeEvent } from '@schooly/analytics';
import { ReportAction, ReportForAssessment, ReportStatuses } from '@schooly/api';
import { useAuth } from '@schooly/components/authentication';
import { HeaderActions } from '@schooly/components/header-actions';
import { DATE_FORMAT } from '@schooly/constants';
import { PublishIcon, UnpublishIcon } from '@schooly/style';
import { newDateTimezoneOffset } from '@schooly/utils/date';
import { format } from 'date-fns';
import React, { FC, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { getReportStatusTextId } from '../utils';

interface ReportsPreviewActionsProps {
  isLoading: boolean;
  onClick: (action: ReportAction) => Promise<void>;
  report?: ReportForAssessment;
}

export const ReportsPreviewActions: FC<ReportsPreviewActionsProps> = ({
  isLoading,
  onClick,
  report,
}) => {
  const { permissions } = useAuth();
  const isReportPublished = report?.report_status === ReportStatuses.Published;
  const isReportDraft = report?.report_status === ReportStatuses.Draft;
  const canPublish = Boolean(permissions?.includes('assessment_manager'));

  const { formatMessage } = useIntl();

  const handleClick = useCallback(async () => {
    if (isReportDraft) return false;

    const action = isReportPublished ? ReportAction.Unpublish : ReportAction.Publish;
    await onClick(action);
    trackAmplitudeEvent({
      name: AnalyticsEventName.ReportStatusChange,
      payload: {
        report_name: report?.name,
        new_status: formatMessage({
          id: getReportStatusTextId(
            isReportPublished ? ReportStatuses.Unpublished : ReportStatuses.Published,
          ),
        }),
      },
    });
    return true;
  }, [formatMessage, isReportDraft, isReportPublished, onClick, report?.name]);

  const title = useMemo(() => {
    if (isReportPublished) {
      return `${formatMessage({ id: 'status-Published' })}${' '} ${
        report?.scheduled_publish_date
          ? format(newDateTimezoneOffset(report.scheduled_publish_date), DATE_FORMAT)
          : ''
      }`;
    }

    return formatMessage({ id: 'status-NotPublished' });
  }, [formatMessage, report?.scheduled_publish_date, isReportPublished]);

  const actions = useMemo(() => {
    if (isReportDraft) return [];

    return [
      {
        titleTextId: formatMessage({
          id: isReportPublished ? 'reports-Unpublish' : 'reports-Publish',
        }),
        handler: handleClick,
        icon: isReportPublished ? (
          <UnpublishIcon />
        ) : (
          <PublishIcon className="reset-svg-currentColor" />
        ),
        pendingTitle: formatMessage({ id: isReportPublished ? 'unPublishing' : 'publishing' }),
      },
    ];
  }, [formatMessage, handleClick, isReportDraft, isReportPublished]);

  if (!report || !canPublish) {
    return null;
  }

  if (isReportDraft)
    return (
      <Tooltip title={formatMessage({ id: 'reports-YouCannonPublish' })}>
        <Stack
          sx={(theme) => ({
            border: theme.mixins.borderValue(),
            borderRadius: theme.spacing(2),
            px: theme.spacing(1.25),
            height: theme.spacing(4),
            justifyContent: 'center',
          })}
        >
          <Typography>{formatMessage({ id: 'status-Draft' })}</Typography>
        </Stack>
      </Tooltip>
    );

  return (
    <HeaderActions
      title={title}
      actions={actions}
      styleType={isReportPublished ? 'success' : isReportDraft ? undefined : 'warning'}
      pending={isLoading}
    />
  );
};
